import { Colors } from "@eai-toolkit-material-ui/theme";
import DoneIcon from "@mui/icons-material/Done";
import { Button, CircularProgress, Paper, Typography } from "@mui/material";
import Alert from "@mui/material/Alert";
import { styled } from "@mui/material/styles";
import {
  clearFileData,
  fetchFileContent,
  fetchFileUrl,
  fetchTiffData,
} from "@store/slices/dataSlice";
import type { AppDispatch, RootState } from "@store/store";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { EXCLUDE_EXTENSIONS, FileType } from "../helpers/utils";
import { DataContext, useCurrentPath } from "../pages/Data";

const PreviewContext = createContext({
  previewTruncated: false,
  setPreviewTruncated: (_previewTruncated: boolean) => {},
});

const PreviewLoading = () => <CircularProgress color="primary" />;

function isMediaOrImageType(fileType?: FileType) {
  return (
    fileType &&
    [
      FileType.VIDEO,
      FileType.AUDIO,
      FileType.IMAGE,
      FileType.IMAGE_TIFF,
    ].includes(fileType)
  );
}

function ImagePreview(props: { name: string }) {
  const dispatch = useDispatch<AppDispatch>();
  const { dataId } = useParams<{ dataId: string }>();
  const path = useCurrentPath({ prefixWithDot: true });
  const { fileUrl, loading } = useSelector((state: RootState) => state.data);

  useEffect(() => {
    if (dataId && path) {
      dispatch(fetchFileUrl({ dataId, mapping: path }));
    }
  }, [dispatch, dataId, path]);

  if (loading || !fileUrl) {
    return <PreviewLoading />;
  }

  return <img src={fileUrl} alt={props.name} />;
}

function TiffPreview() {
  const dispatch = useDispatch<AppDispatch>();
  const { dataId } = useParams<{ dataId: string }>();
  const path = useCurrentPath({ prefixWithDot: true });
  const { tiffData } = useSelector((state: RootState) => state.data);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (dataId && path) {
      dispatch(fetchTiffData({ dataId, filename: path }));
    }
  }, [dispatch, dataId, path]);

  useEffect(() => {
    if (tiffData?.imageData && canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      ctx?.putImageData(tiffData.imageData, 0, 0);
    }
  }, [tiffData]);

  if (!tiffData) {
    return <PreviewLoading />;
  }

  return (
    <canvas ref={canvasRef} width={tiffData.width} height={tiffData.height} />
  );
}

function VideoPreview() {
  const dispatch = useDispatch<AppDispatch>();
  const { dataId } = useParams<{ dataId: string }>();
  const path = useCurrentPath({ prefixWithDot: true });
  const { fileUrl, loading } = useSelector((state: RootState) => state.data);

  useEffect(() => {
    if (dataId && path) {
      dispatch(fetchFileUrl({ dataId, mapping: path }));
    }
  }, [dispatch, dataId, path]);

  if (loading || !fileUrl) {
    return <PreviewLoading />;
  }

  return <video controls preload="auto" src={fileUrl} />;
}

function AudioPreview() {
  const dispatch = useDispatch<AppDispatch>();
  const { dataId } = useParams<{ dataId: string }>();
  const path = useCurrentPath({ prefixWithDot: true });
  const { fileUrl, loading } = useSelector((state: RootState) => state.data);

  useEffect(() => {
    if (dataId && path) {
      dispatch(fetchFileUrl({ dataId, mapping: path }));
    }
  }, [dispatch, dataId, path]);

  if (loading || !fileUrl) {
    return <PreviewLoading />;
  }

  return <audio controls src={fileUrl} />;
}

const StyledTextPreviewContent = styled("div")(() => ({
  whiteSpace: "pre-line",
  overflowWrap: "break-word",
}));

const StyledTruncatedContent = styled(Typography)(({ theme }) => ({
  paddingTop: theme.spacing(3),
}));

function TextPreview() {
  const dispatch = useDispatch<AppDispatch>();
  const { dataId } = useParams<{ dataId: string }>();
  const path = useCurrentPath({ prefixWithDot: true });
  const { fileContent } = useSelector((state: RootState) => state.data);
  const { setPreviewTruncated } = useContext(PreviewContext);

  useEffect(() => {
    if (dataId && path) {
      dispatch(fetchFileContent({ dataId, filepath: path }));
    }
  }, [dispatch, dataId, path]);

  useEffect(() => {
    setPreviewTruncated(Boolean(fileContent && !fileContent.isComplete));
  }, [fileContent, setPreviewTruncated]);

  if (!fileContent) {
    return <PreviewLoading />;
  }

  return (
    <StyledTextPreviewContent>
      {fileContent.content}
      {!fileContent.isComplete && (
        <StyledTruncatedContent variant="body1" color="textSecondary">
          (content truncated)
        </StyledTruncatedContent>
      )}
    </StyledTextPreviewContent>
  );
}

function TabularPreview() {
  const dispatch = useDispatch<AppDispatch>();
  const { dataId } = useParams<{ dataId: string }>();
  const path = useCurrentPath({ prefixWithDot: true });
  const { fileContent } = useSelector((state: RootState) => state.data);
  const { setPreviewTruncated } = useContext(PreviewContext);

  useEffect(() => {
    if (dataId && path) {
      dispatch(fetchFileContent({ dataId, filepath: path }));
    }
  }, [dispatch, dataId, path]);

  useEffect(() => {
    setPreviewTruncated(Boolean(fileContent && !fileContent.isComplete));
  }, [fileContent, setPreviewTruncated]);

  if (!fileContent) {
    return <PreviewLoading />;
  }

  return (
    <pre>
      <code>{fileContent.content}</code>
      {!fileContent.isComplete && (
        <StyledTruncatedContent
          as="div"
          variant="caption"
          color="textSecondary"
        >
          (content truncated)
        </StyledTruncatedContent>
      )}
    </pre>
  );
}

function PreviewType() {
  const { file } = useContext(DataContext);
  if (!file) {
    return null;
  }
  const preventPreview = EXCLUDE_EXTENSIONS.includes(file.extension);
  if (preventPreview) {
    return <div>No Preview Available</div>;
  }
  switch (file.type) {
    case FileType.IMAGE:
      return <ImagePreview name={file.name} />;
    case FileType.IMAGE_TIFF:
      return <TiffPreview />;
    case FileType.TEXT:
      return <TextPreview />;
    case FileType.VIDEO:
      return <VideoPreview />;
    case FileType.AUDIO:
      return <AudioPreview />;
    case FileType.TABULAR:
    default:
      return <TabularPreview />;
  }
}

const StyledPreviewWrapper = styled("div")({
  minWidth: "450px",
  maxWidth: "450px",
  borderRight: "1px solid lightgrey",
  padding: "1rem",
  display: "grid",
  justifyItems: "left",
  alignContent: "center",
  gap: "1rem",
});

const StyledPreviewContent = styled("div")({
  overflow: "auto",
  maxWidth: "100%",
  justifySelf: "normal",
});

const StyledTruncationWarning = styled(Alert)(({ theme }) => ({
  background: Colors.purple[50],
  color: Colors.purple[800],
  "& .MuiAlert-icon": {
    color: theme.palette.primary.main,
  },
}));

const StyledScrollWrapper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "type",
})<{ type?: FileType }>(({ type }) => ({
  padding: isMediaOrImageType(type) ? undefined : "1rem",
  maxHeight: "50vh",
  maxWidth: "100%",
  overflow: "auto",
  fontSize: isMediaOrImageType(type) ? 0 : "1rem",
  "& img, & video, & audio, & canvas": {
    maxWidth: "100%",
  },
  "& pre": {
    margin: 0,
  },
}));

const StyledButtonWrapper = styled(Button)(({ theme }) => ({
  width: theme.spacing(20),
}));

const StyledDownloadedIcon = styled(DoneIcon)(({ theme }) => ({
  // width: theme.spacing(20),
  fontSize: theme.spacing(1.5),
  padding: 0,
  height: theme.spacing(1.5),
}));

const Preview = () => {
  const { file } = useContext(DataContext);
  const { dataId } = useParams<{ dataId: string }>();
  const path = useCurrentPath({ prefixWithDot: true });
  const dispatch = useDispatch<AppDispatch>();
  // TODO: tech debt/todo - we should really be accessing these states from redux data slice
  // or more ideally from react-query
  const [fileDownloadUrlRequested, setFileDownloadUrlRequested] =
    useState(false);
  const [previewTruncated, setPreviewTruncated] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false); // State for download success

  const downloadFile = useCallback(() => {
    if (dataId && path) {
      setFileDownloadUrlRequested(true);
      dispatch(fetchFileUrl({ dataId, mapping: path })).then((result) => {
        if (fetchFileUrl.fulfilled.match(result)) {
          const filename = path.split("/").pop() as string;
          const link = document.createElement("a");
          link.href = result.payload;
          link.download = filename;
          link.dispatchEvent(new MouseEvent("click"));
          setDownloadSuccess(true);
          setFileDownloadUrlRequested(false);
          let downloadTimer = setTimeout(() => setDownloadSuccess(false), 1500);
          return () => {
            clearTimeout(downloadTimer);
          };
        }
      });
    }
  }, [dispatch, dataId, path]);

  useEffect(() => {
    return () => {
      dispatch(clearFileData());
    };
  }, [dispatch]);

  const previewContextValue = useMemo(
    () => ({ previewTruncated, setPreviewTruncated }),
    [previewTruncated],
  );

  if (!file) {
    return null;
  }
  const buttonText = downloadSuccess ? "Downloaded" : "Download file";

  const activeDownload = fileDownloadUrlRequested;
  return (
    <StyledPreviewWrapper className="column">
      <Typography variant="subtitle1">{file.name}</Typography>
      {previewTruncated && (
        <StyledTruncationWarning severity="info">
          This preview shows the beginning of the file&apos;s content.
        </StyledTruncationWarning>
      )}
      <StyledPreviewContent>
        <PreviewContext.Provider value={previewContextValue}>
          <StyledScrollWrapper
            variant={isMediaOrImageType(file.type) ? undefined : "outlined"}
            type={file.type}
          >
            <PreviewType />
          </StyledScrollWrapper>
        </PreviewContext.Provider>
      </StyledPreviewContent>
      <StyledButtonWrapper
        color="primary"
        variant="contained"
        onClick={downloadFile}
        disabled={activeDownload}
        endIcon={downloadSuccess && <StyledDownloadedIcon />}
      >
        {buttonText}
        {activeDownload && (
          <>
            &nbsp;
            <CircularProgress size="1rem" />
          </>
        )}
      </StyledButtonWrapper>
    </StyledPreviewWrapper>
  );
};

export default Preview;
